import { useState, useEffect } from 'react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Textarea } from '../ui/textarea';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../ui/card';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from '../ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Badge } from '../ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../ui/tabs';
import { toast } from 'sonner';
import { axiosInstance } from '../../App';
import { UserPlus, Calendar as CalendarIcon, FileText, Briefcase, Plus, Edit, Trash2, TrendingUp, TrendingDown, Clock, CheckCircle, AlertCircle, ChevronRight, HelpCircle } from 'lucide-react';
import AdvancedQuoteCreator from './AdvancedQuoteCreator';
import QuoteView from './QuoteView';
import { formatDate, formatDateTime, formatTime } from '../../utils/dateUtils';

export default function LeadsManagement() {
  const [leads, setLeads] = useState([]);
  const [leadForms, setLeadForms] = useState([]);
  const [services, setServices] = useState([]);
  const [handymen, setHandymen] = useState([]);
  const [dashboardStats, setDashboardStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [selectedLead, setSelectedLead] = useState(null);
  const [leadDetails, setLeadDetails] = useState(null);
  const [showFormBuilder, setShowFormBuilder] = useState(false);
  const [showQuoteDialog, setShowQuoteDialog] = useState(false);
  const [showConvertDialog, setShowConvertDialog] = useState(false);
  const [showConvertJobDialog, setShowConvertJobDialog] = useState(false);
  const [convertQuoteId, setConvertQuoteId] = useState(null);
  const [showScheduleDialog, setShowScheduleDialog] = useState(false);
  const [showDetailView, setShowDetailView] = useState(false);
  const [existingQuote, setExistingQuote] = useState(null);

  const [formBuilderData, setFormBuilderData] = useState({
    form_name: '',
    form_fields: [
      { name: 'name', type: 'text', required: true, label: 'Full Name', locked: true },
      { name: 'email', type: 'email', required: true, label: 'Email', locked: true },
      { name: 'phone', type: 'phone', required: true, label: 'Phone Number', locked: true }
    ]
  });

  const [quoteData, setQuoteData] = useState({
    items: [{service: '', description: '', hours: 0, rate: 50, amount: 0}],
    tax: 0,
    discount: 0,
    notes: ''
  });

  const [convertData, setConvertData] = useState({
    service_id: '',
    scheduled_date: '',
    address: ''
  });

  const [scheduleData, setScheduleData] = useState({
    visit_date: '',
    handyman_id: '',
    visit_purpose: '',
    notes: ''
  });

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [leadsRes, formsRes, servicesRes, handymenRes, statsRes] = await Promise.all([
        axiosInstance.get('/leads'),
        axiosInstance.get('/lead-forms'),
        axiosInstance.get('/services'),
        axiosInstance.get('/handymen'),
        axiosInstance.get('/leads/dashboard/stats')
      ]);
      setLeads(leadsRes.data);
      setLeadForms(formsRes.data);
      setServices(servicesRes.data);
      setHandymen(handymenRes.data);
      setDashboardStats(statsRes.data);
    } catch (error) {
      toast.error('Failed to load data');
    } finally {
      setLoading(false);
    }
  };

  const handleCreateForm = async (e) => {
    e.preventDefault();
    try {
      await axiosInstance.post('/lead-forms', formBuilderData);
      toast.success('Lead form created successfully');
      setShowFormBuilder(false);
      setFormBuilderData({ 
        form_name: '', 
        form_fields: [
          { name: 'name', type: 'text', required: true, label: 'Full Name', locked: true },
          { name: 'email', type: 'email', required: true, label: 'Email', locked: true },
          { name: 'phone', type: 'phone', required: true, label: 'Phone Number', locked: true }
        ] 
      });
      fetchData();
    } catch (error) {
      toast.error('Failed to create form');
    }
  };

  const addFormField = () => {
    setFormBuilderData({
      ...formBuilderData,
      form_fields: [...formBuilderData.form_fields, { name: '', type: 'text', required: false, locked: false }]
    });
  };

  const updateFormField = (index, key, value) => {
    const newFields = [...formBuilderData.form_fields];
    newFields[index][key] = value;
    setFormBuilderData({ ...formBuilderData, form_fields: newFields });
  };

  const fetchLeadDetails = async (leadId) => {
    try {
      const response = await axiosInstance.get(`/leads/${leadId}/details`);
      setLeadDetails(response.data);
      setShowDetailView(true);
    } catch (error) {
      toast.error('Failed to load lead details');
    }
  };

  const handleScheduleVisit = async () => {
    try {
      const leadData = selectedLead.lead_data || {};
      const visitPayload = {
        lead_id: selectedLead.id,
        customer_name: leadData.full_name || leadData.name || 'Customer',
        customer_phone: leadData.phone || '',
        customer_address: leadData.address || '',
        handyman_id: scheduleData.handyman_id,
        scheduled_date: new Date(scheduleData.visit_date).toISOString(),
        visit_purpose: scheduleData.visit_purpose || 'Site visit',
        notes: scheduleData.notes,
        estimated_duration: 1.0
      };
      
      await axiosInstance.post(`/leads/${selectedLead.id}/schedule-visit`, visitPayload);
      toast.success('Visit scheduled successfully');
      setShowScheduleDialog(false);
      setSelectedLead(null);
      setScheduleData({ visit_date: '', handyman_id: '', visit_purpose: '', notes: '' });
      fetchData();
    } catch (error) {
      toast.error('Failed to schedule visit');
    }
  };

  const handleCreateQuote = async () => {
    try {
      const leadData = selectedLead.lead_data || {};
      const subtotal = quoteData.items.reduce((sum, item) => sum + item.amount, 0);
      const total = subtotal + quoteData.tax - quoteData.discount;

      if (selectedLead?.quote_id) {
        // Update existing quote
        await axiosInstance.patch(`/quotes/${selectedLead.quote_id}`, {
          items: quoteData.items,
          subtotal,
          tax: quoteData.tax,
          discount: quoteData.discount,
          total_amount: total,
          notes: quoteData.notes
        });
        
        // Resend the quote
        await axiosInstance.post(`/quotes/${selectedLead.quote_id}/send`, {
          base_url: window.location.origin
        });
        toast.success('Quote updated and resent successfully');
      } else {
        // Create new quote
        const response = await axiosInstance.post('/quotes', {
          lead_id: selectedLead.id,
          customer_name: leadData.full_name || leadData.name || 'Lead Customer',
          customer_email: leadData.email || 'lead@example.com',
          customer_phone: leadData.phone || '',
          items: quoteData.items,
          subtotal,
          tax: quoteData.tax,
          discount: quoteData.discount,
          total_amount: total,
          notes: quoteData.notes,
          valid_days: 30
        });
        
        // Send the quote
        const quoteId = response.data.quote_id;
        await axiosInstance.post(`/quotes/${quoteId}/send`, {
          base_url: window.location.origin
        });
        toast.success('Quote created and sent successfully');
      }

      setShowQuoteDialog(false);
      setSelectedLead(null);
      setQuoteData({
        items: [{service: '', description: '', hours: 0, rate: 50, amount: 0}],
        tax: 0,
        discount: 0,
        notes: ''
      });
      fetchData();
    } catch (error) {
      toast.error('Failed to process quote');
    }
  };

  const handleConvertToJob = async () => {
    try {
      await axiosInstance.post(`/leads/${selectedLead.id}/convert-to-job`, {
        service_id: convertData.service_id,
        scheduled_date: new Date(convertData.scheduled_date).toISOString(),
        address: convertData.address
      });
      toast.success('Lead converted to job successfully');
      setShowConvertDialog(false);
      setSelectedLead(null);
      fetchData();
    } catch (error) {
      toast.error('Failed to convert lead');
    }
  };

  const updateQuoteItem = (index, key, value) => {
    const newItems = [...quoteData.items];
    newItems[index][key] = value;
    if (key === 'hours' || key === 'rate') {
      newItems[index].amount = newItems[index].hours * newItems[index].rate;
    }
    setQuoteData({ ...quoteData, items: newItems });
  };

  const addQuoteItem = () => {
    setQuoteData({
      ...quoteData,
      items: [...quoteData.items, {service: '', description: '', hours: 0, rate: 50, amount: 0}]
    });
  };

  const getStatusColor = (status) => {
    const colors = {
      new: 'bg-blue-500',
      contacted: 'bg-purple-500',
      visit_scheduled: 'bg-indigo-500',
      visit_completed: 'bg-teal-500',
      quote_sent: 'bg-yellow-500',
      quote_accepted: 'bg-green-500',
      converted_to_job: 'bg-gray-600',
      job_completed: 'bg-emerald-600',
      lost: 'bg-red-500'
    };
    return colors[status] || 'bg-gray-500';
  };

  const handleDeleteLead = async (lead, e) => {
    e.stopPropagation();
    if (!window.confirm(`Are you sure you want to delete the lead "${lead.lead_data?.full_name || lead.lead_data?.name || 'this lead'}"? This action cannot be undone.`)) {
      return;
    }
    try {
      await axiosInstance.delete(`/leads/${lead.id}`);
      toast.success('Lead deleted successfully');
      fetchData();
    } catch (error) {
      toast.error('Failed to delete lead');
    }
  };

  return (
    <div data-testid="leads-management">
      <div className="mb-6">
        <h2 className="text-2xl md:text-4xl font-bold text-gray-900 mb-2" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>
          Leads Management
        </h2>
        <p className="text-gray-600">Manage leads and conversions</p>
      </div>

      {/* Dashboard Stats Cards */}
      {dashboardStats && (
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
          {/* Overview Card */}
          <Card className="bg-white shadow-sm">
            <CardContent className="p-5">
              <h3 className="text-lg font-semibold text-gray-900 mb-4" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>Overview</h3>
              <div className="space-y-2">
                <div className="flex items-center gap-2">
                  <span className="w-2 h-2 rounded-full bg-blue-500"></span>
                  <span className="text-gray-700">New ({dashboardStats.overview.new})</span>
                </div>
                <div className="flex items-center gap-2">
                  <span className="w-2 h-2 rounded-full bg-green-500"></span>
                  <span className="text-gray-700">Assessment complete ({dashboardStats.overview.assessment_complete})</span>
                </div>
                <div className="flex items-center gap-2">
                  <span className="w-2 h-2 rounded-full bg-red-500"></span>
                  <span className="text-gray-700">Overdue ({dashboardStats.overview.overdue})</span>
                </div>
                <div className="flex items-center gap-2">
                  <span className="w-2 h-2 rounded-full bg-yellow-500"></span>
                  <span className="text-gray-700">Unscheduled ({dashboardStats.overview.unscheduled})</span>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* New Requests Card */}
          <Card className="bg-white shadow-sm">
            <CardContent className="p-5">
              <div className="flex items-center justify-between mb-2">
                <h3 className="text-lg font-semibold text-gray-900" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>New requests</h3>
                <ChevronRight className="w-5 h-5 text-gray-400" />
              </div>
              <p className="text-sm text-gray-500 mb-4">{dashboardStats.new_requests.period}</p>
              <div className="flex items-end gap-3">
                <span className="text-4xl font-bold text-gray-900" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>
                  {dashboardStats.new_requests.count}
                </span>
                <span className={`text-sm px-2 py-1 rounded-full flex items-center gap-1 ${
                  dashboardStats.new_requests.change_percent >= 0 
                    ? 'bg-green-100 text-green-700' 
                    : 'bg-red-100 text-red-700'
                }`}>
                  {dashboardStats.new_requests.change_percent >= 0 ? (
                    <TrendingUp className="w-3 h-3" />
                  ) : (
                    <TrendingDown className="w-3 h-3" />
                  )}
                  {dashboardStats.new_requests.change_percent >= 0 ? '+' : ''}{dashboardStats.new_requests.change_percent}%
                </span>
              </div>
            </CardContent>
          </Card>

          {/* Conversion Rate Card */}
          <Card className="bg-white shadow-sm">
            <CardContent className="p-5">
              <div className="flex items-center gap-2 mb-2">
                <h3 className="text-lg font-semibold text-gray-900" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>Conversion rate</h3>
                <HelpCircle className="w-4 h-4 text-gray-400" />
              </div>
              <p className="text-sm text-gray-500 mb-4">Last 30 days</p>
              <div className="flex items-end gap-3">
                <span className="text-4xl font-bold text-gray-900" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>
                  {dashboardStats.conversion_rate_details?.current || dashboardStats.conversion_rate || 0}%
                </span>
                <span className={`text-sm px-2 py-1 rounded-full ${
                  (dashboardStats.conversion_rate_details?.change_percent || 0) >= 0 
                    ? 'bg-green-100 text-green-700' 
                    : 'bg-red-100 text-red-700'
                }`}>
                  {(dashboardStats.conversion_rate_details?.change_percent || 0) >= 0 ? '+' : ''}{dashboardStats.conversion_rate_details?.change_percent || 0}%
                </span>
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      <Tabs defaultValue="leads" className="w-full">
        <TabsList>
          <TabsTrigger value="leads" data-testid="leads-tab">Leads ({leads.length})</TabsTrigger>
          <TabsTrigger value="forms" data-testid="forms-tab">Lead Forms</TabsTrigger>
        </TabsList>

        <TabsContent value="leads" className="space-y-4">
          {loading ? (
            <div className="flex items-center justify-center h-64">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#595959]"></div>
            </div>
          ) : leads.length === 0 ? (
            <Card className="p-8 text-center">
              <UserPlus className="w-12 h-12 mx-auto text-gray-300 mb-4" />
              <p className="text-gray-500">No active leads</p>
              <p className="text-sm text-gray-400 mt-1">Converted and lost leads are automatically archived</p>
            </Card>
          ) : (
            leads.map((lead) => (
              <Card 
                key={lead.id} 
                className="backdrop-blur-sm bg-white/80 shadow-lg border-0 cursor-pointer hover:shadow-xl transition-shadow" 
                data-testid={`lead-card-${lead.id}`}
                onClick={() => fetchLeadDetails(lead.id)}
              >
                <CardHeader>
                  <div className="flex items-start justify-between">
                    <div>
                      <CardTitle>{lead.lead_data?.full_name || lead.lead_data?.name || 'Unnamed Lead'}</CardTitle>
                      <CardDescription>{lead.form_name} | {lead.lead_data?.email}</CardDescription>
                    </div>
                    <div className="flex items-center gap-2">
                      <span className="text-xs text-gray-400 font-mono">#{lead.id.slice(0, 8).toUpperCase()}</span>
                      <Badge className={`${getStatusColor(lead.status)} text-white`}>
                        {lead.status.replace('_', ' ').toUpperCase()}
                      </Badge>
                    </div>
                  </div>
                </CardHeader>
                <CardContent>
                  <div className="grid grid-cols-2 gap-4 mb-4">
                    <div className="text-sm">
                      <span className="text-gray-500">Phone:</span> {lead.lead_data?.phone}
                    </div>
                    <div className="text-sm">
                      <span className="text-gray-500">Created:</span> {formatDate(lead.created_at)}
                    </div>
                    {lead.lead_data?.service_needed && (
                      <div className="text-sm col-span-2">
                        <span className="text-gray-500">Service Needed:</span> {lead.lead_data.service_needed}
                      </div>
                    )}
                    {lead.lead_data?.services_required?.length > 0 && (
                      <div className="text-sm col-span-2">
                        <span className="text-gray-500">Service Required:</span> {lead.lead_data.services_required.join(', ')}
                      </div>
                    )}
                    {lead.lead_data?.service_required_date && (
                      <div className="text-sm">
                        <span className="text-gray-500">Service Date:</span> {formatDate(lead.lead_data.service_required_date)}
                      </div>
                    )}
                    {lead.lead_data?.additional_notes && (
                      <div className="text-sm col-span-2">
                        <span className="text-gray-500">Notes:</span> {lead.lead_data.additional_notes}
                      </div>
                    )}
                  </div>

                  {/* Linked Quote & Job */}
                  {(lead.quote_id || lead.job_id) && (
                    <div className="flex flex-wrap gap-3 mb-3 text-sm">
                      {lead.quote_id && (
                        <a
                          href={`/admin/quotes`}
                          onClick={(e) => { e.stopPropagation(); }}
                          className="inline-flex items-center gap-1 text-blue-600 hover:text-blue-800 hover:underline font-medium"
                          data-testid={`lead-quote-link-${lead.id}`}
                        >
                          <FileText size={14} />
                          Quote {lead.quote_number || `#${lead.quote_id.slice(0, 8).toUpperCase()}`}
                        </a>
                      )}
                      {lead.job_id && (
                        <a
                          href={`/admin/jobs`}
                          onClick={(e) => { e.stopPropagation(); }}
                          className="inline-flex items-center gap-1 text-green-600 hover:text-green-800 hover:underline font-medium"
                          data-testid={`lead-job-link-${lead.id}`}
                        >
                          <Briefcase size={14} />
                          Job #{lead.job_id.slice(0, 8).toUpperCase()}
                        </a>
                      )}
                    </div>
                  )}

                  <div className="flex flex-wrap gap-2">
                    {/* Schedule Visit - hide once already scheduled */}
                    {(lead.status === 'new' || lead.status === 'contacted') && lead.status !== 'visit_scheduled' && (
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={(e) => {
                          e.stopPropagation();
                          setSelectedLead(lead);
                          setShowScheduleDialog(true);
                        }}
                        data-testid={`schedule-visit-${lead.id}`}
                      >
                        <CalendarIcon size={14} className="mr-1" />
                        Schedule Visit
                      </Button>
                    )}

                    {/* Create Quote / Convert to Quote - hide once quote approved or converted/completed */}
                    {!['converted', 'cancelled', 'lost', 'converted_to_job', 'job_completed', 'quote_accepted', 'quote_approved'].includes(lead.status) && (
                      lead.status === 'visit_completed' ? (
                        <Button
                          size="sm"
                          variant="outline"
                          className="border-teal-500 text-teal-700 hover:bg-teal-50"
                          onClick={(e) => {
                            e.stopPropagation();
                            setSelectedLead(lead);
                            fetchLeadDetails(lead.id);
                          }}
                          data-testid={`convert-to-quote-${lead.id}`}
                        >
                          <FileText size={14} className="mr-1" />
                          Convert to Quote
                        </Button>
                      ) : (
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={(e) => {
                            e.stopPropagation();
                            setSelectedLead(lead);
                            setExistingQuote(null);
                            setShowQuoteDialog(true);
                          }}
                          data-testid={`create-quote-${lead.id}`}
                        >
                          <FileText size={14} className="mr-1" />
                          Create Quote
                        </Button>
                      )
                    )}

                    {/* Edit Quote - only when quote exists */}
                    {lead.status === 'quote_sent' && lead.quote_id && (
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={async (e) => {
                          e.stopPropagation();
                          try {
                            const quoteRes = await axiosInstance.get(`/quotes/${lead.quote_id}`);
                            setExistingQuote(quoteRes.data);
                            setSelectedLead(lead);
                            setShowQuoteDialog(true);
                          } catch (error) {
                            toast.error('Failed to load quote');
                          }
                        }}
                        data-testid={`edit-quote-${lead.id}`}
                      >
                        <Edit size={14} className="mr-1" />
                        Edit Quote
                      </Button>
                    )}

                    {/* Convert to Job - ONLY after quote approved AND no job yet */}
                    {(lead.status === 'quote_accepted' || lead.status === 'quote_approved') && lead.quote_id && !lead.job_id && (
                      <Button
                        size="sm"
                        onClick={(e) => {
                          e.stopPropagation();
                          setConvertQuoteId(lead.quote_id);
                          setShowConvertJobDialog(true);
                        }}
                        data-testid={`convert-job-${lead.id}`}
                      >
                        <Briefcase size={14} className="mr-1" />
                        Convert to Job
                      </Button>
                    )}

                    {/* Delete Button - Always visible */}
                    <Button
                      size="sm"
                      variant="outline"
                      className="text-red-600 hover:text-red-700 hover:bg-red-50"
                      onClick={(e) => handleDeleteLead(lead, e)}
                      data-testid={`delete-lead-${lead.id}`}
                    >
                      <Trash2 size={14} className="mr-1" />
                      Delete
                    </Button>
                  </div>
                </CardContent>
              </Card>
            ))
          )}
        </TabsContent>

        <TabsContent value="forms">
          <div className="mb-6 flex gap-3">
            {/* Create Standard Service Request Form */}
            <Button 
              onClick={async () => {
                try {
                  await axiosInstance.post('/lead-forms', {
                    form_name: 'Service Request Form',
                    form_fields: [
                      { name: 'full_name', type: 'text', required: true, label: 'Full Name', locked: true },
                      { name: 'email', type: 'email', required: true, label: 'Email ID', locked: true },
                      { name: 'phone', type: 'phone', required: true, label: 'Phone Number', locked: true },
                      { name: 'address_line', type: 'text', required: true, label: 'Address Line', locked: true },
                      { name: 'city', type: 'text', required: true, label: 'City', locked: true },
                      { name: 'state', type: 'text', required: true, label: 'State', locked: true },
                      { name: 'pincode', type: 'text', required: true, label: 'Pin Code', locked: true },
                      { name: 'service_required_date', type: 'date', required: true, label: 'Service Required Date', locked: true },
                      { name: 'service_required', type: 'text', required: true, label: 'Service Required', locked: true },
                      { name: 'additional_notes', type: 'textarea', required: false, label: 'Additional Notes', locked: false }
                    ]
                  });
                  toast.success('Standard Service Request Form created successfully!');
                  fetchData();
                } catch (error) {
                  toast.error('Failed to create form');
                }
              }}
              data-testid="create-standard-form-btn"
              className="bg-[#595959] hover:bg-[#404040]"
            >
              <Plus size={16} className="mr-2" />
              Create Standard Lead Form
            </Button>
            
            <Dialog open={showFormBuilder} onOpenChange={setShowFormBuilder}>
              <DialogTrigger asChild>
                <Button variant="outline" data-testid="create-form-btn">
                  <Plus size={16} className="mr-2" />
                  Create Custom Form
                </Button>
              </DialogTrigger>
              <DialogContent className="max-w-2xl">
                <DialogHeader>
                  <DialogTitle>Build Lead Form</DialogTitle>
                  <DialogDescription>Create a custom form to capture leads</DialogDescription>
                </DialogHeader>
                <form onSubmit={handleCreateForm} className="space-y-4">
                  <div className="space-y-2">
                    <Label>Form Name</Label>
                    <Input
                      value={formBuilderData.form_name}
                      onChange={(e) => setFormBuilderData({...formBuilderData, form_name: e.target.value})}
                      placeholder="Contact Form"
                      data-testid="form-name-input"
                      required
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Form Fields</Label>
                    <div className="space-y-3">
                      {formBuilderData.form_fields.map((field, index) => (
                        <div key={index} className={`p-3 border rounded ${field.locked ? 'bg-gray-50' : 'bg-white'}`}>
                          {field.locked ? (
                            <div className="flex items-center justify-between">
                              <div className="flex-1">
                                <div className="flex items-center gap-2">
                                  <Badge variant="secondary" className="text-xs">Required</Badge>
                                  <span className="font-medium">{field.label}</span>
                                </div>
                                <p className="text-xs text-gray-500 mt-1">Type: {field.type}</p>
                              </div>
                              <span className="text-xs text-gray-400">Default Field</span>
                            </div>
                          ) : (
                            <div className="grid grid-cols-4 gap-2">
                              <Input
                                placeholder="Field Name"
                                value={field.name}
                                onChange={(e) => updateFormField(index, 'name', e.target.value)}
                              />
                              <Select value={field.type} onValueChange={(value) => updateFormField(index, 'type', value)}>
                                <SelectTrigger>
                                  <SelectValue />
                                </SelectTrigger>
                                <SelectContent>
                                  <SelectItem value="text">Text</SelectItem>
                                  <SelectItem value="email">Email</SelectItem>
                                  <SelectItem value="phone">Phone</SelectItem>
                                  <SelectItem value="textarea">Textarea</SelectItem>
                                </SelectContent>
                              </Select>
                              <div className="flex items-center">
                                <input
                                  type="checkbox"
                                  checked={field.required}
                                  onChange={(e) => updateFormField(index, 'required', e.target.checked)}
                                  className="mr-2"
                                />
                                <span className="text-sm">Required</span>
                              </div>
                              <Button 
                                type="button" 
                                variant="outline" 
                                size="sm"
                                onClick={() => {
                                  const newFields = formBuilderData.form_fields.filter((_, i) => i !== index);
                                  setFormBuilderData({ ...formBuilderData, form_fields: newFields });
                                }}
                              >
                                Remove
                              </Button>
                            </div>
                          )}
                        </div>
                      ))}
                    </div>
                    <Button type="button" variant="outline" onClick={addFormField} className="w-full">
                      <Plus size={14} className="mr-1" /> Add Custom Field
                    </Button>
                  </div>
                  <Button type="submit" className="w-full" data-testid="submit-form-btn">
                    Create Form
                  </Button>
                </form>
              </DialogContent>
            </Dialog>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {leadForms.map((form) => (
              <Card key={form.id} className="backdrop-blur-sm bg-white/80 shadow-lg border-0">
                <CardHeader>
                  <CardTitle>{form.form_name || form.name}</CardTitle>
                  <CardDescription>{(form.form_fields || form.fields || []).length} fields</CardDescription>
                </CardHeader>
                <CardContent className="space-y-3">
                  <div className="flex items-center gap-2">
                    <Badge className={form.is_active ? 'bg-green-500' : 'bg-gray-500'}>
                      {form.is_active ? 'Active' : 'Inactive'}
                    </Badge>
                  </div>
                  
                  {/* Shareable Link */}
                  <div className="p-3 bg-gray-100 rounded border border-gray-300">
                    <Label className="text-xs text-gray-600 mb-1 block">Shareable Link:</Label>
                    <div className="flex items-center gap-2">
                      <Input
                        value={`${window.location.origin}/lead-form/${form.id}`}
                        readOnly
                        className="text-sm"
                        data-testid={`form-link-${form.id}`}
                      />
                      <Button
                        size="sm"
                        onClick={() => {
                          navigator.clipboard.writeText(`${window.location.origin}/lead-form/${form.id}`);
                          toast.success('Link copied to clipboard!');
                        }}
                        data-testid={`copy-link-${form.id}`}
                      >
                        Copy
                      </Button>
                    </div>
                  </div>
                  
                  <Button
                    variant="outline"
                    className="w-full"
                    onClick={() => window.open(`/lead-form/${form.id}`, '_blank')}
                    data-testid={`preview-form-${form.id}`}
                  >
                    Preview Form
                  </Button>
                </CardContent>
              </Card>
            ))}
          </div>
        </TabsContent>
      </Tabs>

      {/* Schedule Visit Dialog */}
      <Dialog open={showScheduleDialog} onOpenChange={setShowScheduleDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Schedule Visit</DialogTitle>
            <DialogDescription>Select date, time, and assign a handyman for the visit</DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label>Visit Date & Time</Label>
              <Input
                type="datetime-local"
                value={scheduleData.visit_date}
                onChange={(e) => setScheduleData({...scheduleData, visit_date: e.target.value})}
                data-testid="visit-date-input"
              />
            </div>
            <div>
              <Label>Assign Handyman</Label>
              <Select 
                value={scheduleData.handyman_id} 
                onValueChange={(value) => setScheduleData({...scheduleData, handyman_id: value})}
              >
                <SelectTrigger data-testid="handyman-select">
                  <SelectValue placeholder="Select handyman" />
                </SelectTrigger>
                <SelectContent>
                  {handymen.filter(h => h.is_available).map((handyman) => (
                    <SelectItem key={handyman.id} value={handyman.id}>
                      {handyman.name} - {handyman.skills.join(', ')}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Visit Purpose</Label>
              <Input
                value={scheduleData.visit_purpose}
                onChange={(e) => setScheduleData({...scheduleData, visit_purpose: e.target.value})}
                placeholder="e.g., Site inspection"
                data-testid="visit-purpose-input"
              />
            </div>
            <div>
              <Label>Notes (Optional)</Label>
              <Textarea
                value={scheduleData.notes}
                onChange={(e) => setScheduleData({...scheduleData, notes: e.target.value})}
                placeholder="Additional notes..."
                data-testid="visit-notes-input"
              />
            </div>
            <Button onClick={handleScheduleVisit} className="w-full" data-testid="confirm-schedule-btn">
              Confirm Schedule
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Advanced Quote Creator Dialog */}
      <AdvancedQuoteCreator
        open={showQuoteDialog}
        onOpenChange={(open) => {
          setShowQuoteDialog(open);
          if (!open) {
            setSelectedLead(null);
            setExistingQuote(null);
          }
        }}
        lead={selectedLead}
        existingQuote={existingQuote}
        onSuccess={fetchData}
      />

      {/* Enhanced Convert to Job - uses QuoteView's Convert Dialog */}
      <QuoteView
        open={showConvertJobDialog}
        onOpenChange={(open) => {
          setShowConvertJobDialog(open);
          if (!open) setConvertQuoteId(null);
        }}
        quoteId={convertQuoteId}
        autoOpenConvert={true}
        onRefresh={fetchData}
      />

      {/* Lead Detail View Dialog */}
      <Dialog open={showDetailView} onOpenChange={setShowDetailView}>
        <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="text-2xl">Lead Details</DialogTitle>
            <DialogDescription>Comprehensive view of lead information and activities</DialogDescription>
          </DialogHeader>
          {leadDetails && (
            <div className="space-y-6">
              {/* Customer Information */}
              <Card>
                <CardHeader>
                  <CardTitle className="text-lg">Customer Information</CardTitle>
                </CardHeader>
                <CardContent className="grid grid-cols-2 gap-4">
                  <div>
                    <Label className="text-gray-500">Name</Label>
                    <p className="font-medium">{leadDetails.lead?.lead_data?.full_name || leadDetails.lead?.lead_data?.name || 'N/A'}</p>
                  </div>
                  <div>
                    <Label className="text-gray-500">Email</Label>
                    <p className="font-medium">{leadDetails.lead?.lead_data?.email || 'N/A'}</p>
                  </div>
                  <div>
                    <Label className="text-gray-500">Phone</Label>
                    <p className="font-medium">{leadDetails.lead?.lead_data?.phone || 'N/A'}</p>
                  </div>
                  <div>
                    <Label className="text-gray-500">Status</Label>
                    <Badge className={`${getStatusColor(leadDetails.lead?.status)} text-white`}>
                      {leadDetails.lead?.status?.replace('_', ' ').toUpperCase()}
                    </Badge>
                  </div>
                  {leadDetails.lead?.lead_data?.service_needed && (
                    <div className="col-span-2">
                      <Label className="text-gray-500">Service Needed</Label>
                      <p className="font-medium">{leadDetails.lead.lead_data.service_needed}</p>
                    </div>
                  )}
                  {leadDetails.lead?.lead_data?.services_required?.length > 0 && (
                    <div className="col-span-2">
                      <Label className="text-gray-500">Service Required</Label>
                      <p className="font-medium">{leadDetails.lead.lead_data.services_required.join(', ')}</p>
                    </div>
                  )}
                  {leadDetails.lead?.lead_data?.service_required_date && (
                    <div>
                      <Label className="text-gray-500">Service Required Date</Label>
                      <p className="font-medium">{formatDate(leadDetails.lead.lead_data.service_required_date)}</p>
                    </div>
                  )}
                  {leadDetails.lead?.lead_data?.additional_notes && (
                    <div className="col-span-2">
                      <Label className="text-gray-500">Additional Notes</Label>
                      <p className="font-medium">{leadDetails.lead.lead_data.additional_notes}</p>
                    </div>
                  )}
                  {leadDetails.lead?.lead_data?.address && (
                    <div className="col-span-2">
                      <Label className="text-gray-500">Address</Label>
                      <p className="font-medium">{leadDetails.lead.lead_data.address}</p>
                    </div>
                  )}
                </CardContent>
              </Card>

              {/* Visit Schedules */}
              {leadDetails.visits && leadDetails.visits.length > 0 && (
                <Card>
                  <CardHeader>
                    <CardTitle className="text-lg">Visit Schedules</CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-4">
                    {leadDetails.visits.map((visit) => (
                      <div key={visit.id} className="p-4 border rounded-lg bg-gray-50">
                        <div className="flex justify-between items-start mb-2">
                          <div>
                            <p className="font-medium">{visit.handyman_name}</p>
                            <p className="text-sm text-gray-600">{visit.visit_purpose}</p>
                          </div>
                          <Badge className={visit.status === 'completed' ? 'bg-green-500 text-white' : 'bg-blue-500 text-white'}>
                            {visit.status?.toUpperCase()}
                          </Badge>
                        </div>
                        <p className="text-sm text-gray-600">
                          Scheduled: {formatDateTime(visit.scheduled_date)}
                        </p>
                        {visit.notes && (
                          <p className="text-sm text-gray-500 mt-1">Notes: {visit.notes}</p>
                        )}

                        {/* Visit Report */}
                        {visit.visit_report && (
                          <div className="mt-3 border-t pt-3">
                            <h4 className="text-sm font-semibold text-gray-800 mb-2">Visit Report</h4>
                            <div className="space-y-2">
                              {visit.visit_report.line_items?.map((item, idx) => (
                                <div key={idx} className="p-2 bg-white rounded border text-sm">
                                  <p className="font-medium">{item.description}</p>
                                  {item.notes && <p className="text-gray-500 text-xs mt-1">{item.notes}</p>}
                                  {item.photos && item.photos.length > 0 && (
                                    <div className="flex gap-1 mt-1 flex-wrap">
                                      {item.photos.map((photo, pi) => (
                                        <img key={pi} src={photo} alt="" className="w-12 h-12 object-cover rounded border cursor-pointer" onClick={() => window.open(photo, '_blank')} />
                                      ))}
                                    </div>
                                  )}
                                </div>
                              ))}
                              {visit.visit_report.general_notes && (
                                <p className="text-xs text-gray-600 italic mt-2">Notes: {visit.visit_report.general_notes}</p>
                              )}
                              {visit.visit_report.general_photos?.length > 0 && (
                                <div className="flex gap-1 mt-1 flex-wrap">
                                  {visit.visit_report.general_photos.map((photo, i) => (
                                    <img key={i} src={photo} alt="" className="w-12 h-12 object-cover rounded border cursor-pointer" onClick={() => window.open(photo, '_blank')} />
                                  ))}
                                </div>
                              )}
                            </div>
                            {leadDetails.lead?.quote_id && !leadDetails.lead?.job_id ? (
                              <Button
                                size="sm"
                                className="mt-3 bg-blue-600 hover:bg-blue-700 text-white"
                                data-testid={`convert-to-job-${visit.id}`}
                                onClick={() => {
                                  setConvertQuoteId(leadDetails.lead.quote_id);
                                  setShowDetailView(false);
                                  setShowConvertJobDialog(true);
                                }}
                              >
                                <Briefcase size={14} className="mr-1" />
                                Convert to Job
                              </Button>
                            ) : !leadDetails.lead?.quote_id ? (
                              <Button
                                size="sm"
                                className="mt-3 bg-[#595959] hover:bg-[#444]"
                                data-testid={`convert-report-to-quote-${visit.id}`}
                                onClick={() => {
                                  const prefillItems = visit.visit_report.line_items?.map(item => ({
                                    description: item.description,
                                    notes: item.notes || '',
                                    photos: item.photos || []
                                  })) || [];
                                  setSelectedLead({
                                    ...leadDetails.lead,
                                    prefill_line_items: prefillItems,
                                    prefill_notes: visit.visit_report.general_notes || '',
                                    prefill_photos: visit.visit_report.general_photos || []
                                  });
                                  setShowDetailView(false);
                                  setShowQuoteDialog(true);
                                }}
                              >
                                Convert to Quote
                              </Button>
                            ) : null}
                          </div>
                        )}
                      </div>
                    ))}
                  </CardContent>
                </Card>
              )}

              {/* Quotes */}
              {leadDetails.quotes && leadDetails.quotes.length > 0 && (
                <Card>
                  <CardHeader>
                    <CardTitle className="text-lg">Quotes</CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-3">
                    {leadDetails.quotes.map((quote) => (
                      <div key={quote.id} className="p-3 border rounded-lg bg-gray-50">
                        <div className="flex justify-between items-start mb-2">
                          <div>
                            <p className="font-medium">Quote #{quote.id.substring(0, 8)}</p>
                            <p className="text-sm text-gray-600">Version {quote.version}</p>
                          </div>
                          <div className="text-right">
                            <p className="font-bold text-lg">${quote.total_amount}</p>
                            <Badge>{quote.status}</Badge>
                          </div>
                        </div>
                        <div className="text-sm text-gray-600">
                          <p>Items: {quote.items?.length || 0}</p>
                          <p>Valid until: {formatDate(quote.valid_until)}</p>
                        </div>
                      </div>
                    ))}
                  </CardContent>
                </Card>
              )}

              {/* Activity Timeline */}
              <Card>
                <CardHeader>
                  <CardTitle className="text-lg">Activity Timeline</CardTitle>
                </CardHeader>
                <CardContent>
                  {leadDetails.activities && leadDetails.activities.length > 0 ? (
                    <div className="space-y-3">
                      {leadDetails.activities.map((activity) => (
                        <div key={activity.id} className="flex gap-3 pb-3 border-b last:border-b-0">
                          <div className="mt-1">
                            <div className="w-2 h-2 rounded-full bg-gray-1000"></div>
                          </div>
                          <div className="flex-1">
                            <p className="font-medium text-sm">{activity.description}</p>
                            <p className="text-xs text-gray-500">
                              {formatDateTime(activity.created_at)}
                            </p>
                          </div>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <p className="text-sm text-gray-500">No activities yet</p>
                  )}
                </CardContent>
              </Card>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}